import React from "react";
import { Card, Form, Alert } from 'react-bootstrap';
import { connect } from "react-redux";
import { ValidatableForm } from "../../components/ValidatableForm";
import { QUOTE_STEPS, COMPONENTS } from "../../AppConstants";
import { validationUtils } from "../../validation/Validation";
import { IQuoteStepState, IQuoteStepComponentProps } from "../../components/interfaces";
import Utilities from "../../classes/Utilities";
import ButtonContinue from "../../components/ButtonContinue";
import { IQuoteApiClient, ILOBApiClient, IPaths, ICoverage } from "../../classes/Domain";
import QuoteActions from "../../state/appActions";
import { IVehicle, ICoverageConfig, IDriver, IContactInfo, IDiscountOption } from "../../classes/Domain";
import QuoteStepBase from "../../components/QuoteStepBase";
import { QuickQuoteStep } from "../QuickQuote/QuickQuoteStepLayout";
import { IAppState } from "../../state/interfaces";
import SwiperCore, { Pagination } from 'swiper';
import { getDefaultLiabilityCoverages, hasQuickQuoteDiscounts } from "../../config/StateQuoteConfig";
import { isGddAttestationEligible } from "../../config/qualitasGddAttestation";

interface IAppStateUpdate {
    quoteUid: string,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    coverageConfig: ICoverageConfig,
    contactInfo: IContactInfo,
    discounts: IDiscountOption,
    isNonOwners: boolean,
    quickStep: string,
    stateCode: string
}

interface IState extends IQuoteStepState {
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle | null>,
    coverageConfig: ICoverageConfig,
    activeVehicleIndex: number,
    activeDriverIndex: number,
    canAddNewVehicle: boolean,
    canAddNewDriver: boolean,
    continueClick: boolean,
    contactInfo: IContactInfo,
    discounts: IDiscountOption,
    isNonOwners: boolean
}

export interface IProps extends IQuoteStepComponentProps {
    stateCode: string,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    rateError: string | null,
    PATHS: IPaths;
}

const QUALITAS_DISCOUNT_LABEL =
    "I have had zero or one traffic violation or at-fault accident which resulted only in property damage in the last 3 years.";

class QuickDiscount extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);

        SwiperCore.use([Pagination]);

        this.state = {
            drivers: [{} as IDriver],
            vehicles: [{} as IVehicle],
            isNonOwners: false,
            coverageConfig: {
                liabilityCoverages: getDefaultLiabilityCoverages(this.props.stateCode),
                addonCoverages: [],
                vehicleCoverages: [{
                    vehicleId: 0,
                    coverages: [{} as ICoverage]
                }]
            } as ICoverageConfig,
            activeVehicleIndex: 0,
            activeDriverIndex: 0,
            canAddNewVehicle: false,
            canAddNewDriver: false,
            continueClick: false,
            contactInfo: { phone: '', email: '' } as IContactInfo,
            discounts: {
                zeroOrOneViolationLast3Years: false
            } as IDiscountOption,
            updatingStore: false,
            errors: null
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();
    }

    subscribeToValidation() {
        validationUtils.subscribeToValidationFailure(QUOTE_STEPS.QUICK_DISCOUNT,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, continueClick: false, errors: validationData });
            });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            nextProps.meta.propsSource = "";
            const vehicles = nextProps.vehicles;
            const drivers = nextProps.drivers;
            const coverageConfig = nextProps.coverageConfig;
            const contactInfo = nextProps.contactInfo;
            const discounts = nextProps.discounts;
            const isNonOwners = nextProps.isNonOwners;
            const initialSlide = nextProps.initialSlide;


            if (vehicles || drivers) {
                let canAddNewVehicle = nextProps.canAddNewVehicle;
                if (!canAddNewVehicle && vehicles.length > 0 && vehicles.every(x => x != null)) {
                    canAddNewVehicle = true;
                }

                let canAddNewDriver = nextProps.canAddNewDriver;
                if (!canAddNewDriver && drivers.length > 0 && drivers.every(x => x.age > 0 && x.maritalStatus && x.gender && x.currentLicenseStatus)) {
                    canAddNewDriver = true;
                }

                const mergedDiscounts = { ...(discounts ?? prevState.discounts) } as IDiscountOption;
                if (!isGddAttestationEligible(drivers)) {
                    mergedDiscounts.zeroOrOneViolationLast3Years = false;
                }

                const nextState = {
                    ...prevState,
                    vehicles: vehicles,
                    canAddNewVehicle: canAddNewVehicle ?? prevState.canAddNewVehicle,
                    drivers: drivers,
                    canAddNewDriver: canAddNewDriver ?? prevState.canAddNewDriver,
                    coverageConfig: coverageConfig ?? prevState.coverageConfig,
                    contactInfo: contactInfo ?? prevState.contactInfo,
                    discounts: mergedDiscounts,
                    isNonOwners: isNonOwners ?? prevState.isNonOwners,
                    initialSlide: initialSlide
                };

                return nextState;
            }
        }

        return prevState;
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(QUOTE_STEPS.QUICK_DISCOUNT);
    }

    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true }, () => {
            const updatedVehicles = [...this.state.vehicles];
            const updatedDrivers = [...this.state.drivers];
            const coverageConfig = this.state.coverageConfig;
            const attestationEligible = hasQuickQuoteDiscounts(this.props.stateCode)
                && isGddAttestationEligible(updatedDrivers);
            const discounts = {
                ...this.state.discounts,
                zeroOrOneViolationLast3Years: attestationEligible
                    ? !!this.state.discounts?.zeroOrOneViolationLast3Years
                    : false
            } as IDiscountOption;
            const contactInfo = this.state.contactInfo;
            const isNonOwners = this.state.isNonOwners;

            this.props.updateAppState({ quoteUid: this.props.quoteUid, vehicles: updatedVehicles, drivers: updatedDrivers, coverageConfig: coverageConfig, discounts: discounts, contactInfo: contactInfo, isNonOwners: isNonOwners, quickStep: QUOTE_STEPS.QUICK_DISCOUNT, stateCode: this.props.stateCode } as IAppStateUpdate)
                .then(() => this.props.history.push(this.props.PATHS.RESULT),
                    () => {
                        this.setState({ ...this.state, updatingStore: false });
                    }
                );
        });
    }

    handleZeroOrOneViolationLast3Years = (evt: any) => {
        if (!isGddAttestationEligible(this.state.drivers)) {
            return;
        }
        const _discounts = { ...this.state.discounts, zeroOrOneViolationLast3Years: evt.target.checked };
        this.setState({ ...this.state, discounts: _discounts });
    };

    render() {
        const showCaGoodDriver = hasQuickQuoteDiscounts(this.props.stateCode);
        const attestationEligible = isGddAttestationEligible(this.state.drivers);

        return (
            <QuickQuoteStep id={this.props.id} stateCode={this.props.stateCode} >
                {
                    this.props.rateError
                        ?
                        <div>
                            <Alert variant={"warning"}>
                                <h5 className="text-center">
                                    <strong className="text-uppercase">Sorry we can't continue</strong>
                                    <br />
                                    {this.props.rateError}
                                </h5>
                            </Alert>
                        </div>
                        :
                        null
                }
                {
                    <div>
                        {showCaGoodDriver && (
                            <Card className="quick-quote-data">
                                <Card.Body>
                                    <Card className="drivers-card">
                                        <Card.Body>
                                            <Card.Title>
                                                {
                                                    'DISCOUNTS'
                                                }
                                            </Card.Title>
                                            <ValidatableForm hasErrors={null != this.state.errors}>
                                                <Form.Group className="col">
                                                    <div className="break-2" />
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="zeroOrOneViolationLast3Years"
                                                        className={attestationEligible ? "" : "discount-attestation-disabled"}
                                                    >
                                                        <Form.Check.Input
                                                            type="checkbox"
                                                            checked={attestationEligible && !!this.state.discounts?.zeroOrOneViolationLast3Years}
                                                            onChange={this.handleZeroOrOneViolationLast3Years}
                                                            disabled={!attestationEligible || this.state.updatingStore}
                                                            isValid={attestationEligible}
                                                        />
                                                        <Form.Check.Label className={attestationEligible ? "lable-grey" : "lable-grey text-muted"}>
                                                            {QUALITAS_DISCOUNT_LABEL}
                                                        </Form.Check.Label>
                                                    </Form.Check>
                                                    {!attestationEligible && (
                                                        <div className="text-danger small mt-2">
                                                            Based on the driving history you entered, this discount attestation does not apply.
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </ValidatableForm>
                                        </Card.Body>
                                    </Card>
                                </Card.Body>
                            </Card>
                        )}

                        <div className="btnContainer">
                            <ButtonContinue
                                label="Get Rate"
                                disabled={this.state.updatingStore}
                                showLoading={this.state.updatingStore}
                                onClick={this.next} />
                        </div>
                    </div>
                }
            </QuickQuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: IProps) => {
    var contactInfo = state.contactInfo;
    if (contactInfo) {
        contactInfo.email = null;
    }

    return {
        quoteUid: state.quoteUid,
        stateCode: state.stateCode,
        vehicles: state.vehicles.length > 0 ? state.vehicles : [{} as IVehicle],
        coverageConfig: state.coverageConfig,
        drivers: state.drivers.length > 0 ? state.drivers : [{} as IDriver],
        contactInfo: contactInfo,
        discounts: state.discountOption,
        isNonOwners: state.isNonOwners,
        rateError: state.rateError,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE }
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.QUICK_DISCOUNT)),
        updateAppState: (update: IAppStateUpdate) => dispatch(quoteActions.updateQuickData(update.quoteUid, update.vehicles, update.drivers, update.coverageConfig, update.discounts, update.contactInfo, update.isNonOwners, update.quickStep, update.stateCode))
    };
}

export default connect(mapProps, mapDispatch)(QuickDiscount);
